if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        // aria-disabled blocks the double submit; data-busy tells the disabled
        // contract in kashira-theme.css that this is the in-flight state, so the
        // button keeps its maroon ground and the spinner stays legible (spec 17).
        this.submitButton.setAttribute('data-busy', '');
        // Captured before any morph so both the loading label and the 1.2s
        // revert restore the real rest label, never "Adding…".
        if (this.submitButtonText && !this.submitButton.classList.contains('kashira-atc-added')) {
          this.kashiraRestLabel = this.submitButtonText.textContent;
        }
        // Spec: spinner only when the request outlasts 400ms — a fast add must
        // not flash one. The label swaps with it so the button is never
        // wordless while it spins.
        this.kashiraSpinnerTimer = setTimeout(() => {
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner')?.classList.remove('hidden');
          const adding = window.variantStrings?.kashiraAdding;
          if (adding && this.submitButtonText) this.submitButtonText.textContent = adding;
        }, 400);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Spec morph (design 03 L138): ADDED ✓ holds 1.2s, THEN the
              // drawer auto-opens — the confirmation is read before it moves.
              this.kashiraShowAdded();
              setTimeout(() => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }, 1200);
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            clearTimeout(this.kashiraSpinnerTimer);
            this.submitButton.removeAttribute('data-busy');
            this.submitButton.classList.remove('loading');
            // The success morph is already on the button by the time `finally`
            // runs — one signal per action means the loading label steps aside
            // for it rather than overwriting it.
            if (!this.submitButton.classList.contains('kashira-atc-added')) this.kashiraRestoreLabel();
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      kashiraRestoreLabel() {
        this.submitButton.querySelector('.kashira-atc-added__tick')?.remove();
        if (this.submitButtonText && this.kashiraRestLabel) this.submitButtonText.textContent = this.kashiraRestLabel;
      }

      /* ref 17 L71: a 13px stroked tick at stroke-width 2.6, drawn beside the
         word — not a ✓ inside the translated label, which cannot take a stroke
         weight and would have to be re-typed into every locale. Sizes are
         presentational attributes so the glyph stays sane if the CSS has not
         landed; the 9px gap is the button's own. */
      kashiraTick() {
        const NS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('class', 'kashira-atc-added__tick');
        svg.setAttribute('width', '13');
        svg.setAttribute('height', '13');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '2.6');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        const path = document.createElementNS(NS, 'path');
        path.setAttribute('d', 'M5 13l5 5L20 7');
        svg.appendChild(path);
        return svg;
      }

      /* Spec morph: rest -> (spinner past 400ms) -> success 1.2s -> revert.
         Width is locked by `button--full-width`, so no label swap reflows it.
         ONE signal per action: this runs instead of a toast, never alongside. */
      kashiraShowAdded() {
        if (!this.submitButtonText) return;
        const added = window.variantStrings?.kashiraAdded;
        if (!added) return;
        this.kashiraRestLabel = this.kashiraRestLabel || this.submitButtonText.textContent;
        clearTimeout(this.kashiraAddedTimer);
        this.submitButton.querySelector('.kashira-atc-added__tick')?.remove();
        this.submitButton.classList.add('kashira-atc-added');
        this.submitButtonText.textContent = added;
        // Before the label, matching the reference order; inserted as a sibling
        // so the button's own gap lays the pair out.
        this.submitButtonText.parentNode.insertBefore(this.kashiraTick(), this.submitButtonText);
        // The visual button-text swap alone is not announced; mirror it into a
        // polite live region so assistive tech confirms the add (spec 17 a11y).
        this.kashiraAnnounce(added);
        this.kashiraAddedTimer = setTimeout(() => {
          this.submitButton.classList.remove('kashira-atc-added');
          this.kashiraRestoreLabel();
        }, 1200);
      }

      kashiraAnnounce(message) {
        if (!this.kashiraLiveRegion) {
          this.kashiraLiveRegion = document.createElement('p');
          this.kashiraLiveRegion.className = 'visually-hidden';
          this.kashiraLiveRegion.setAttribute('aria-live', 'polite');
          this.kashiraLiveRegion.setAttribute('role', 'status');
          this.appendChild(this.kashiraLiveRegion);
        }
        // Clear first so repeated adds re-announce the same text.
        this.kashiraLiveRegion.textContent = '';
        window.requestAnimationFrame(() => {
          this.kashiraLiveRegion.textContent = message;
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // The price suffix ("— ₹24,850") lives in a sibling span the label swaps
        // never touch; a sold-out/unavailable label must not keep quoting one.
        const priceSuffix = this.submitButton.querySelector('.kashira-atc-price');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          priceSuffix?.classList.add('hidden');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          priceSuffix?.classList.remove('hidden');
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
